import React from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';

interface GlassNavLinkProps {
  name: string;
  href: string;
  hasDropdown?: boolean;
  isOpen?: boolean;
  active?: boolean;
  onPress?: () => void;
}

// Primary nav links share a fixed width so they read as a uniform row (84px = w-21).
const GLASS_NAV_FIXED_WIDTH_NAMES = new Set(['Sell', 'Buy', 'Agents']);

export const GlassNavLink: React.FC<GlassNavLinkProps> = ({
  name,
  href,
  hasDropdown,
  isOpen,
  active,
  onPress,
}) => {
  const className = cn(
    'inline-flex h-10 flex-row cursor-pointer items-center justify-center gap-2 rounded-xl border-0 px-3 no-underline transition-[color,background-color] duration-200 hover:bg-od-bg-scrim/20 focus:bg-od-bg-scrim/20 focus:outline-none',
    GLASS_NAV_FIXED_WIDTH_NAMES.has(name) && 'w-21',
    isOpen
      ? 'bg-od-bg-scrim/20 text-od-content-inactive-accent-brand! hover:text-od-content-inactive-accent-brand! focus:text-od-content-inactive-accent-brand!'
      : 'bg-transparent text-od-content-inverse-primary! hover:text-od-content-inverse-primary/70! focus:text-od-content-inverse-primary/70!',
  );

  const content = (
    <>
      <Text as="span" className="text-label-md text-inherit">
        {name}
      </Text>
      {hasDropdown && (
        <Row
          aria-hidden="true"
          className={cn(
            'h-6 w-6 items-center justify-center transition duration-200 hover:opacity-90',
            isOpen ? 'rotate-180 opacity-100' : 'opacity-60',
          )}
        >
          <IconChevronDown size={24} stroke={2} aria-hidden="true" />
        </Row>
      )}
    </>
  );

  const sharedProps = {
    className,
    'aria-label': hasDropdown ? `${name} menu` : `View ${name}`,
    'aria-current': active ? ('page' as const) : undefined,
    analyticsName: `cosmos-glass-nav-link-${name.toLowerCase()}`,
  };

  if (hasDropdown) {
    return (
      <Box {...sharedProps} onClick={() => onPress?.()} aria-expanded={isOpen} aria-haspopup="true">
        {content}
      </Box>
    );
  }

  return (
    <Box {...sharedProps} href={href}>
      {content}
    </Box>
  );
};
